import { useCallback } from 'react';
import { usePlan } from '../providers/PlanProvider';
import { useUpgradeModal } from '../providers/UpgradeModalProvider';

export const usePlanPermissions = () => {
  const { planData } = usePlan();
  const { showUpgradeModal } = useUpgradeModal();

  // Function to check permission based on backend data
  const hasPermission = useCallback((feature) => {
    if (!planData?.permissions) {
      // If no plan data, assume no permissions for safety
      return false;
    }

    // Convert feature to the format used in backend permissions
    const permissionKey = `can_${feature}`;
    // const permissionKey = `can_${feature.replace(/([A-Z])/g, '_$1').toLowerCase()}`;
    return planData.permissions[permissionKey] || false;

  }, [planData]);

  // Validation functions for trial restrictions with modal
  const validatePageNavigation = useCallback(() => {
    if (hasPermission('go_to_next_page')) {
      return { allowed: true };
    }

    showUpgradeModal('pagination');
    return {
      allowed: false,
      reason: 'TRIAL_PAGE_LIMIT',
      message: 'Usuários trial podem ver apenas a primeira página de resultados'
    };
  }, [hasPermission, showUpgradeModal]);

  const validateMessageSending = useCallback(() => {
    if (hasPermission('send_message')) {
      return { allowed: true };
    }

    showUpgradeModal('messaging', 'Seu plano atual não permite o envio de mensagens');
    return {
      allowed: false,
      reason: 'TRIAL_MESSAGE_LIMIT',
      message: 'Usuários trial não podem enviar mensagens para talentos'
    };
  }, [hasPermission, showUpgradeModal]);

  const validateSelfieLetterAccess = useCallback(() => {
    if (hasPermission('watch_selfie_letter')) {
      return { allowed: true };
    }

    showUpgradeModal('selfieLetter', 'Seu plano atual não permite assistir a Selfie Letter');
    return {
      allowed: false,
      reason: 'TRIAL_VIDEO_LIMIT',
      message: 'Usuários trial não podem assistir selfie-letters'
    };
  }, [hasPermission, showUpgradeModal]);

  // Helper function to check if plan is trial
  const isTrialPlan = useCallback(() => {
    if (!planData) return true;
    return planData.plan?.trial === true;
  }, [planData]);

  const isValidateAdvancedSearchAccess = useCallback(() => {
    if (hasPermission('apply_advanced_filters')) {
      return { allowed: true };
    }
  }, [hasPermission]);

  const hasSupportWhatsAppAccess = useCallback(() => {
    return hasPermission('contact_support_whatsapp');
  }, [hasPermission]);

  const validateShareTalentProfile = useCallback(() => {
    if (hasPermission('share_talent_profile')) {
      return { allowed: true };
    }

    showUpgradeModal('shareProfile', 'Seu plano atual não permite compartilhar perfis de talentos');
    return {
      allowed: false,
      reason: 'PLAN_SHARE_LIMIT',
      message: 'Seu plano atual não permite compartilhar perfis de talentos'
    };
  }, [hasPermission, showUpgradeModal]);

  return {
    hasPermission,
    isTrialPlan,
    validatePageNavigation,
    validateMessageSending,
    validateSelfieLetterAccess,
    isValidateAdvancedSearchAccess,
    hasSupportWhatsAppAccess,
    validateShareTalentProfile,
  };
};
